// Bloqueio progressivo de login no cliente. Dissuasao contra tentativas repetidas
// feitas pela propria tela (nao substitui rate-limit no backend, que ja bloqueia a
// conta na 3a falha). Persistido em localStorage: um mapa por email + um contador
// global do navegador (pega script que fica trocando de email).

export const STORAGE_KEY = 'siapen:login_throttle';

// Sem nenhuma tentativa e sem bloqueio ativo por esse tempo -> contadores zeram.
const RESET_AFTER_MS = 30 * 60 * 1000;

// Tiers por email. O backend bloqueia a conta na 3a falha, entao o foco aqui e
// dar a pausa/aviso antes disso (o aviso da 2a falha e tratado na tela).
const PER_EMAIL_TIERS = [
  { minFails: 2, lockMs: 20 * 1000 },
  { minFails: 3, lockMs: 60 * 1000 },
  { minFails: 4, lockMs: 5 * 60 * 1000 },
];

// Contador global do navegador (independe do email).
const GLOBAL_TIERS = [
  { minFails: 10, lockMs: 60 * 1000 },
  { minFails: 20, lockMs: 5 * 60 * 1000 },
  { minFails: 35, lockMs: 15 * 60 * 1000 },
];

const now = () => Date.now();

export const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

// Ofuscacao (djb2 -> base36). NAO e seguranca: so evita deixar emails em claro
// no localStorage.
const hashKey = (value) => {
  const str = String(value);
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return h.toString(36);
};

const readStore = () => {
  try {
    if (typeof localStorage === 'undefined') return {};
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (_) {
    return {};
  }
};

const writeStore = (data) => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (_) {
    // storage cheio/indisponivel: throttle vira no-op e o login continua funcionando
  }
};

const lockMsFor = (fails, tiers) => (
  tiers.reduce((acc, tier) => (fails >= tier.minFails ? tier.lockMs : acc), 0)
);

export const formatRemaining = (ms) => {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return minutes > 0 ? `${minutes}min ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
};

const emptyEntry = () => ({ fails: 0, firstAt: 0, lastAt: 0, lockUntil: 0 });

// Zera o registro se ficou muito tempo parado e sem bloqueio ativo.
const decay = (entry, ts) => (
  entry.lastAt && ts - entry.lastAt > RESET_AFTER_MS && (entry.lockUntil || 0) < ts
    ? emptyEntry()
    : entry
);

export const getLockState = (email) => {
  const store = readStore();
  const key = hashKey(normalizeEmail(email));
  const perEmail = store.byEmail?.[key] || emptyEntry();
  const global = store.global || emptyEntry();

  const lockUntil = Math.max(perEmail.lockUntil || 0, global.lockUntil || 0);
  const remainingMs = Math.max(0, lockUntil - now());

  return {
    locked: remainingMs > 0,
    remainingMs,
    remainingLabel: formatRemaining(remainingMs),
    lockUntil,
    failureCount: perEmail.fails || 0,
    globalFailureCount: global.fails || 0,
  };
};

// Registra uma falha de credencial. serverRetryAfterMs (de um 429) entra por max().
export const registerFailure = (email, { serverRetryAfterMs = null } = {}) => {
  const store = readStore();
  const key = hashKey(normalizeEmail(email));
  const ts = now();
  store.byEmail = store.byEmail || {};

  const perEmail = decay(store.byEmail[key] || emptyEntry(), ts);
  const global = decay(store.global || emptyEntry(), ts);

  perEmail.fails += 1;
  perEmail.firstAt = perEmail.firstAt || ts;
  perEmail.lastAt = ts;

  global.fails += 1;
  global.firstAt = global.firstAt || ts;
  global.lastAt = ts;

  const serverLockUntil = Number.isFinite(serverRetryAfterMs) && serverRetryAfterMs > 0
    ? ts + serverRetryAfterMs
    : 0;
  const perEmailTierMs = lockMsFor(perEmail.fails, PER_EMAIL_TIERS);
  const globalTierMs = lockMsFor(global.fails, GLOBAL_TIERS);

  perEmail.lockUntil = Math.max(
    perEmail.lockUntil || 0,
    perEmailTierMs ? ts + perEmailTierMs : 0,
    serverLockUntil,
  );
  global.lockUntil = Math.max(global.lockUntil || 0, globalTierMs ? ts + globalTierMs : 0);

  store.byEmail[key] = perEmail;
  store.global = global;
  writeStore(store);
  return getLockState(email);
};

// Sucesso no login: apaga o registro do email e alivia o contador global.
export const clearFailures = (email) => {
  const store = readStore();
  const key = hashKey(normalizeEmail(email));
  if (store.byEmail && store.byEmail[key]) {
    delete store.byEmail[key];
  }
  store.global = emptyEntry();
  writeStore(store);
};

// Helper para testes/depuracao.
export const clearAll = () => writeStore({});
